import { all, call, put, takeLatest } from 'redux-saga/effects';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { Action, Resource } from '@topconagriculture/permissions';
import { checkPermissions } from '@topconagriculture/casbin-react';

import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { getFarmsListRequest } from '../farms/farms.actions';
import { createLotType, deleteLotType, getLotTypes, updateLotType } from './lotTypes.actions';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';

export function* getLotTypesSaga(action: ReturnType<typeof getLotTypes.started>) {
  yield put(showLoader(LoadersNames.getLotTypes));
  try {
    const canReadLotTypes = yield call(checkPermissions, {
      resource: Resource.FEED_SETTINGS_LOTTYPES,
      action: Action.READ,
    });
    if (canReadLotTypes) {
      const result = yield call(api.lotTypes.getLotTypes);
      yield put(
        getLotTypes.done({
          params: action.payload,
          result,
        }),
      );
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getLotTypes));
}

export function* createLotTypeSaga(action: ReturnType<typeof createLotType.started>) {
  yield put(showLoader(LoadersNames.createLotType));
  try {
    const { payload, meta } = action;
    const { data, formName } = payload;
    const result = yield call(api.lotTypes.createLotType, data, formName);
    yield put(
      createLotType.done({
        params: payload,
        result,
      }),
    );
    yield put(notificationSuccess('lots:lot_type_create_success'));
    if (meta) {
      if (meta.onSuccess && typeof meta.onSuccess === 'function') {
        meta.onSuccess(result);
      }
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createLotType));
}

export function* updateLotTypeSaga(action: ReturnType<typeof updateLotType.started>) {
  try {
    const { payload, meta } = action;
    const { data, formName } = payload;
    const result = yield call(api.lotTypes.updateLotType, data.id!, data, formName);
    yield put(
      updateLotType.done({
        params: payload,
        result,
      }),
    );
    yield put(notificationSuccess('lots:lot_type_edit_success'));
    if (meta && meta.onSuccess && typeof meta.onSuccess === 'function') {
      meta.onSuccess(result);
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* deleteLotTypeSaga(action: ReturnType<typeof deleteLotType.started>) {
  try {
    const { id, formName } = action.payload;
    yield call(api.lotTypes.deleteLotType, id, formName);
    yield put(
      deleteLotType.done({
        params: action.payload,
        result: id,
      }),
    );
    successHandler(action.meta);
    yield put(notificationSuccess('lots:lot_type_delete_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* watchLotTypesSaga() {
  yield all([
    yield takeLatest(getFarmsListRequest.done, getLotTypesSaga),
    yield takeLatest(getLotTypes.started, getLotTypesSaga),
    yield takeLatest(createLotType.started, createLotTypeSaga),
    yield takeLatest(updateLotType.started, updateLotTypeSaga),
    yield takeLatest(deleteLotType.started, deleteLotTypeSaga),
  ]);
}
